import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import CssBaseline from '@mui/material/CssBaseline';

// Componentes
import LandingPage from './components/LandingPage/LandingPage';
import Login from './components/Login/Login';
import TwoFactorAuth from './components/TwoFactorAuth/TwoFactorAuth'; // Removida extensão .tsx
import GoogleAuthCallback from './components/Auth/GoogleAuthCallback';
import Layout from './components/Layout/Layout';
import Dashboard from './components/Dashboard/Dashboard.tsx';
import Clientes from './components/Clientes/Clientes';
import ClienteFicha from './components/Clientes/ClienteFicha';
import ClienteFichaPrint from './components/Clientes/ClienteFichaPrint';
import Agendamento from './components/Agendamento/Agendamento';
import AgendamentoCalendarioFuncional from './components/Agendamento/AgendamentoCalendarioFuncional';
import MeuCadastro from './components/MeuCadastro/MeuCadastro';
import MinhaExperiencia from './components/MinhaExperiencia/MinhaExperiencia';
import ExperienciaFicha from './components/Experiencias/ExperienciaFicha';

import Evolucoes from './components/Evolucoes/Evolucoes';
import Despesas from './components/Despesas/Despesas';
import Recebimentos from './components/Recebimentos/Recebimentos';
import TiposDespesa from './components/TiposDespesa/TiposDespesa.tsx';
import Usuarios from './components/Usuarios/Usuarios';
import GruposUsuarios from './components/GruposUsuarios/GruposUsuarios';
import NiveisAcesso from './components/NiveisAcesso/NiveisAcesso';
import LogSistema from './components/LogSistema/LogSistema';
import GerarAgenda from './components/GerarAgenda/GerarAgenda.tsx';
import GerarMensalidade from './components/GerarMensalidade/GerarMensalidade';
import Experiencias from './components/Experiencias/Experiencias.tsx';
import Configuracoes from './components/Configuracoes/Configuracoes';
import RaioXFinanceiro from './components/RaioXFinanceiro/RaioXFinanceiro';
import SuporteTecnico from './components/SuporteTecnico/SuporteTecnico';
import AssinaturaStripe from './components/Assinatura/AssinaturaStripe';
import CadastroAssinaturaStripe from './components/Assinatura/CadastroAssinaturaStripe';
import AssinaturaSucesso from './components/Assinatura/AssinaturaSucesso';
import AssinaturaCancelado from './components/Assinatura/AssinaturaCancelado';

// Componente para rotas protegidas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Carregando...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Componente para rotas públicas (quando já está logado, redireciona)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Carregando...</div>;
  }
  
  return !isAuthenticated ? children : <Navigate to="/app/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <CssBaseline />
      <Routes>
        {/* Página inicial - Landing Page */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Rotas públicas */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        {/* Link de redefinição abre o Login e o modal via token na URL */}
        <Route 
          path="/reset-senha" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/2fa" 
          element={
            // Permitir acesso à rota 2FA mesmo sem autenticação completa
            <TwoFactorAuth />
          } 
        />
        <Route path="/experiencia/:token" element={<MinhaExperiencia />} />
        <Route path="/meu-cadastro/:token" element={<MeuCadastro />} />
        <Route path="/assinar" element={<CadastroAssinaturaStripe />} />
        <Route path="/assinatura/sucesso" element={<AssinaturaSucesso />} />
        <Route path="/assinatura/cancelado" element={<AssinaturaCancelado />} />
        
        {/* Callback do Google Calendar OAuth */}
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        
        {/* Rota para impressão da ficha do cliente - SEM Layout */}
        <Route path="/imprimir/cliente/:id" element={<ClienteFichaPrint />} />
        
        {/* Rotas protegidas */}
        <Route path="/app" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clientes" element={<Clientes />} />
          <Route path="clientes/:id" element={<ClienteFicha />} />
          <Route path="clientes/:id/print" element={<ClienteFichaPrint />} />
          <Route path="agendamento" element={<Agendamento />} />
          <Route path="agendamento-calendario" element={<AgendamentoCalendarioFuncional />} />
          <Route path="meu-cadastro" element={<MeuCadastro />} />
          <Route path="minha-experiencia" element={<MinhaExperiencia />} />
          <Route path="evolucoes" element={<Evolucoes />} />
          <Route path="despesas" element={<Despesas />} />
          <Route path="despesas/:id" element={<Despesas />} />
          <Route path="despesas/editar/:id" element={<Despesas />} />
          <Route path="recebimentos" element={<Recebimentos />} />
          <Route path="tipos-despesa" element={<TiposDespesa />} />
          <Route path="usuarios" element={<Usuarios />} />
          <Route path="grupos-usuarios" element={<GruposUsuarios />} />
          <Route path="niveis-acesso" element={<NiveisAcesso />} />
          <Route path="log-sistema" element={<LogSistema />} />
          <Route path="logs" element={<LogSistema />} />
          <Route path="gerar-agenda" element={<GerarAgenda />} />
          <Route path="gerar-mensalidades" element={<GerarMensalidade />} />
          <Route path="experiencias" element={<Experiencias />} />
          <Route path="experiencia-ficha/:id" element={<ExperienciaFicha />} />
          <Route path="raiox-financeiro" element={<RaioXFinanceiro />} />
          <Route path="configuracoes" element={<Configuracoes />} />
          <Route path="suporte-tecnico" element={<SuporteTecnico />} />
          <Route path="assinatura" element={<AssinaturaStripe />} />
        </Route>
        
        {/* Rota catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;